import React, { useRef, useState } from "react";
import { Stage, Layer, Line, Rect, Circle, Group } from "react-konva";
import DrawLegend from "Components/DrawLegend";
import Konva from "konva";

const Garden = () => {
  type drawModes = "pointer" | "move" | "rectangle" | "polygon";
  const [drawMode, setDrawMode] = useState<drawModes>("move");
  const [globalMousePosition, setGlobalMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [dragRect, setDragRect] = useState({ width: 0, height: 0, x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [mouseClicked, setMouseClicked] = useState(false);
  const [initialGlobalMousePosition, setInitialGlobalMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [drawRectangle, setDrawRectangle] = useState(false);
  const [drawPolygonPoints, setDrawPolygonPoints] = useState<
    Array<{ x: number; y: number }>
  >([]);
  const [scale, setScale] = useState(1);
  const [shapes, setShapes] = useState<
    Array<{
      type: drawModes;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
      points?: number[];
      collision?: boolean;
    }>
  >([]);
  const [selectedShapeIndex, setSelectedShapeIndex] = useState<number | null>(
    null,
  );
  const setDraw = (mode: "pointer" | "move" | "rectangle" | "polygon") => {
    setDrawMode(mode);
    setDragRect({ width: 0, height: 0, x: 0, y: 0 });
    setDrawRectangle(false);
  };

  const stageRef = useRef<Konva.Stage | null>(null);
  const lastPointerPosition = useRef<{ x: number; y: number } | null>(null);

  const checkShapeCollision = (newShape: {
    type: string;
    x: number;
    y: number;
    width?: number;
    height?: number;
    points?: { x: number; y: number }[];
    ignoreIndex?: number;
  }) => {
    let index = 0;
    for (const shape of shapes) {
      if (
        shape.type === "rectangle" &&
        newShape.type === "rectangle" &&
        newShape.ignoreIndex !== index
      ) {
        const rect1 = {
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
        };
        const rect2 = {
          x: newShape.x,
          y: newShape.y,
          width: newShape.width ?? 0,
          height: newShape.height ?? 0,
        };

        if (
          !rect1.x ||
          !rect2.x ||
          !rect2.y ||
          !rect1.y ||
          !rect1.width ||
          !rect1.height
        )
          return false;

        if (
          rect1.x < rect2.x + rect2.width &&
          rect1.x + rect1.width > rect2.x &&
          rect1.y < rect2.y + rect2.height &&
          rect1.y + rect1.height > rect2.y
        ) {
          return true;
        }
      }
      index++;
    }
    return false;
  };

  const getWorldPosition = () => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };

    const pointer = stage.getPointerPosition();
    if (!pointer) return { x: 0, y: 0 };

    return {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY(),
    };
  };

  const handleStageMouseMove = () => {
    setGlobalMousePosition(getWorldPosition());

    if (mouseClicked && drawMode === "pointer") {
      handlePan();
    }
  };

  const handleWheel = (e: any) => {
    if (drawMode !== "pointer") return;
    e.evt.preventDefault();

    const stage = stageRef.current;
    const oldScale = stage?.scaleX();
    const pointer = stage?.getPointerPosition();

    if (!pointer || !stage || !oldScale) return;

    const mousePointTo = {
      x: (pointer.x - stage?.x()) / oldScale,
      y: (pointer.y - stage?.y()) / oldScale,
    };

    // how to scale? Zoom in? Or zoom out?
    let direction = e.evt.deltaY > 0 ? 1 : -1;

    // when we zoom on trackpad, e.evt.ctrlKey is true
    // in that case lets revert direction
    if (e.evt.ctrlKey) {
      direction = -direction;
    }

    const scaleBy = 1.01;
    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

    stage.scale({ x: newScale, y: newScale });

    setScale(newScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };

    stage.position(newPos);
  };

  const handlePan = () => {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();

    const lastPos = lastPointerPosition.current;
    if (!pointer || !stage || !lastPos) return;

    const dx = pointer.x - lastPos.x;
    const dy = pointer.y - lastPos.y;

    stage.position({ x: stage.x() + dx, y: stage.y() + dy });
    lastPointerPosition.current = { x: pointer.x, y: pointer.y };
    stage.batchDraw();
  };

  const handleStageClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (drawMode === "polygon" && event.target !== event.currentTarget) {
      return;
    }

    setMouseClicked(true);

    const worldPoint = getWorldPosition();
    setInitialGlobalMousePosition({ x: worldPoint.x, y: worldPoint.y });

    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (drawMode === "pointer") {
      lastPointerPosition.current = pointer
        ? { x: pointer.x, y: pointer.y }
        : null;
    }

    if (drawMode === "rectangle" && !drawRectangle) {
      setDrawRectangle(true);
      setGlobalMousePosition(worldPoint);
    } else if (drawMode === "rectangle" && drawRectangle) {
      setDrawRectangle(false);
      const worldPoint = getWorldPosition();
      const collision = !checkShapeCollision({
        type: "rectangle",
        x: Math.min(initialGlobalMousePosition.x, worldPoint.x),
        y: Math.min(initialGlobalMousePosition.y, worldPoint.y),
        width: Math.abs(worldPoint.x - initialGlobalMousePosition.x),
        height: Math.abs(worldPoint.y - initialGlobalMousePosition.y),
      });

      if (collision) {
        setShapes([
          ...shapes,
          {
            type: "rectangle",
            x: Math.min(initialGlobalMousePosition.x, worldPoint.x),
            y: Math.min(initialGlobalMousePosition.y, worldPoint.y),
            width: Math.abs(worldPoint.x - initialGlobalMousePosition.x),
            height: Math.abs(worldPoint.y - initialGlobalMousePosition.y),
          },
        ]);
      }
    }

    if (drawMode === "move" && !drawRectangle) {
      setDrawRectangle(true);
      setGlobalMousePosition(worldPoint);
    }

    if (drawMode === "polygon") {
      setDrawPolygonPoints([
        ...drawPolygonPoints,
        { x: worldPoint.x, y: worldPoint.y },
      ]);
    }
  };

  const handleStageMouseUp = () => {
    setMouseClicked(false);
    lastPointerPosition.current = null;
  };

  const toggleShapeIndex = (index: number) => {
    if (drawMode === "pointer" && selectedShapeIndex !== index)
      setSelectedShapeIndex(index);
    else if (drawMode === "pointer" && selectedShapeIndex === index)
      setSelectedShapeIndex(null);
  };

  const createPolygon = () => {
    setShapes([
      ...shapes,
      {
        type: "polygon",
        points: drawPolygonPoints.flatMap((point) => [point.x, point.y]),
      },
    ]);

    setDrawPolygonPoints([]);
  };

  return (
    <div>
      <DrawLegend setDraw={setDraw} drawMode={drawMode} />

      <Stage
        width={window.innerWidth - 200}
        height={window.innerHeight}
        ref={stageRef}
        onMouseMove={handleStageMouseMove}
        onWheel={handleWheel}
        onMouseUp={handleStageMouseUp}
        onMouseDown={handleStageClick}
      >
        <Layer>
          {!!drawPolygonPoints?.length && drawMode === "polygon" && (
            <Group>
              {drawPolygonPoints.map((point, index) => (
                <Circle
                  key={index}
                  radius={10}
                  x={point.x}
                  y={point.y}
                  fill="gray"
                  stroke="black"
                  fillEnabled={true}
                  strokeWidth={4}
                  onClick={() => createPolygon()}
                />
              ))}
              <Line
                zIndex={0}
                points={drawPolygonPoints.flatMap((point) => [
                  point.x,
                  point.y,
                ])}
                fill="gray"
                stroke="black"
                dash={[10, 5]}
                listening={false}
              />
            </Group>
          )}
        </Layer>
        <Layer>
          {drawRectangle && drawMode === "rectangle" && (
            <Rect
              zIndex={1}
              x={Math.min(initialGlobalMousePosition.x, globalMousePosition.x)}
              y={Math.min(initialGlobalMousePosition.y, globalMousePosition.y)}
              width={Math.abs(
                globalMousePosition.x - initialGlobalMousePosition.x,
              )}
              height={Math.abs(
                globalMousePosition.y - initialGlobalMousePosition.y,
              )}
              fill={
                checkShapeCollision({
                  type: "rectangle",
                  x: Math.min(
                    initialGlobalMousePosition.x,
                    globalMousePosition.x,
                  ),
                  y: Math.min(
                    initialGlobalMousePosition.y,
                    globalMousePosition.y,
                  ),
                  width: Math.abs(
                    globalMousePosition.x - initialGlobalMousePosition.x,
                  ),
                  height: Math.abs(
                    globalMousePosition.y - initialGlobalMousePosition.y,
                  ),
                })
                  ? "rgba(255, 0, 0, 0.5)"
                  : "rgba(0, 0, 255, 0.5)"
              }
              stroke={
                checkShapeCollision({
                  type: "rectangle",
                  x: Math.min(
                    initialGlobalMousePosition.x,
                    globalMousePosition.x,
                  ),
                  y: Math.min(
                    initialGlobalMousePosition.y,
                    globalMousePosition.y,
                  ),
                  width: Math.abs(
                    globalMousePosition.x - initialGlobalMousePosition.x,
                  ),
                  height: Math.abs(
                    globalMousePosition.y - initialGlobalMousePosition.y,
                  ),
                })
                  ? "red"
                  : "blue"
              }
              dash={[10, 5]}
              strokeWidth={2}
            />
          )}

          {shapes.map((shape, index) => {
            if (shape.type === "rectangle") {
              return (
                <Rect
                  key={index}
                  x={shape.x}
                  y={shape.y}
                  width={shape.width}
                  height={shape.height}
                  fill={
                    shape.collision
                      ? "rgba(255,0,0,0.5)"
                      : selectedShapeIndex !== index
                        ? "rgba(0, 0, 255, 0.5)"
                        : "rgba(0, 255, 0, 0.5)"
                  }
                  stroke={isDragging ? "lightblue" : "blue"}
                  dash={isDragging ? [10, 5] : undefined}
                  strokeWidth={2}
                  onClick={() =>
                    drawMode === "pointer" && toggleShapeIndex(index)
                  }
                  onDragEnd={(e) => {
                    const shape = shapes[index];

                    if (shape.collision) {
                      shape.collision = false;
                      e.target.position({ x: dragRect.x, y: dragRect.y });
                      setShapes(
                        shapes.map((currentShape, shapeIndex) =>
                          shapeIndex === index
                            ? { ...currentShape, x: dragRect.x, y: dragRect.y }
                            : currentShape,
                        ),
                      );
                      setIsDragging(false);
                    }

                    setShapes(
                      shapes.map((currentShape, shapeIndex) =>
                        shapeIndex === index
                          ? {
                              ...currentShape,
                              x: e.target.x(),
                              y: e.target.y(),
                            }
                          : currentShape,
                      ),
                    );
                    setIsDragging(false);
                  }}
                  onDragStart={(e) => {
                    if (!shape.width || !shape.height || !shape.x || !shape.y)
                      return;
                    setDragRect({
                      width: shape.width,
                      height: shape.height,
                      x: shape.x,
                      y: shape.y,
                    });
                    setIsDragging(true);
                  }}
                  onDragMove={(e: any) => {
                    const collision = checkShapeCollision({
                      type: "rectangle",
                      x: e.target.x(),
                      y: e.target.y(),
                      width: shape.width,
                      height: shape.height,
                      ignoreIndex: index,
                    });

                    setShapes(
                      shapes.map((currentShape, shapeIndex) =>
                        shapeIndex === index
                          ? { ...currentShape, collision }
                          : currentShape,
                      ),
                    );
                  }}
                  draggable={drawMode === "move"}
                />
              );
            } else if (shape.type === "polygon") {
              return (
                <Line
                  key={index}
                  points={shape.points}
                  fill="gray"
                  stroke="black"
                  closed
                />
              );
            }
            return null;
          })}
        </Layer>
      </Stage>
    </div>
  );
};

export default Garden;
